// ink-component
import React, { MouseEvent } from 'react';
import Dropdown from '../../../Dropdown/Dropdown';
import Button from '../../../Button/Button';
import HStack from '../../../HStack/HStack';

export type FilterType = Record<string, any>;

interface FilterItem {
  label: string;
  value: string;
  id: string;
}

interface FilterGroup {
  label: string;
  key: string;
  items: FilterItem[];
}

export interface FilterItems {
  groups: FilterGroup[];
}

export interface FilterProps<T extends FilterType> {
  /** Unique identifier */
  id: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Items to populate the filter */
  items: FilterItems;
  /** Optional custom trigger passed through as a render prop */
  trigger?: (triggerProps: { onClick: (event: MouseEvent) => void; activeTrigger: boolean }) => React.ReactElement;
  /** Function to get a filter value. Comes from useLedger hook. */
  getFilterValue: (filterName: keyof T) => unknown;
  /** Function to set a filter value. Comes from useLedger hook. */
  setFilterValue: (filterName: keyof T, value: string) => void;
  /** Function to replace all filter values with the provided ones. Comes from useLedger hook. */
  replaceFilters: (filters: T) => void;
  /** Function to clear filter values. Comes from useLedger hook. */
  clearFilters: () => void;
  /** Array represeting current filters. Comes from useLedger hook. */
  filters: T;
}

/**
 * @purpose Filter component for Ledger implementations
 * @group ledger
 * @categories table
 */
const Filter = <T extends FilterType>(props: FilterProps<T>) => {
  return (
    <HStack>
      <Dropdown
        trigger={triggerProps => {
          return props.trigger ? (
            <props.trigger {...triggerProps} />
          ) : (
            <Dropdown.Trigger {...triggerProps}>Filter</Dropdown.Trigger>
          );
        }}
      >
        {props.items.groups.map((group, i) => (
          <React.Fragment key={i}>
            <Dropdown.Header>{group.label}</Dropdown.Header>
            {group.items.map(option => (
              <Dropdown.Item
                key={option.id}
                render={dropdownProps => (
                  <Dropdown.Belt>
                    <Dropdown.Radio
                      {...dropdownProps}
                      id={option.id}
                      label={option.label}
                      value={option.value}
                      checked={option.value === props.getFilterValue(group.key)}
                      name={group.label}
                      onClick={() => {
                        props.setFilterValue(group.key, option.value);
                      }}
                    />
                  </Dropdown.Belt>
                )}
              />
            ))}
          </React.Fragment>
        ))}
      </Dropdown>
      {Object.keys(props.filters).length > 0 && (
        <Button type="link" onClick={props.clearFilters}>
          Clear filters
        </Button>
      )}
    </HStack>
  );
};

Filter.defaultProps = {
  id: undefined,
  'data-testid': undefined,
  items: [],
  trigger: undefined,
  getFilterValue: undefined,
  setFilterValue: undefined,
  replaceFilters: undefined,
  clearFilters: undefined,
  filters: {},
};

Filter.displayName = 'LedgerWrapper.Filter';

export default Filter;
